import { useNavigate } from "react-router-dom";
import { TitlelessCard, ODataGrid, Breakpoints, ResponsiveGridColumn } from "../../../../components";
import CTEyeService from "../../../../services/CTEyeService";
import { useDebouncedFilter } from '../../hooks';
import { formatDateTime, getEndOfMonth } from "../../../../app/dateHelpers";
import { DashboardSectionHeader, UsageSparkline } from "./";
import { DotBadge } from "../";

const getClientSummaries = async (oDataQuery: string) => {
  return await new CTEyeService().getClientsSummariesOData(oDataQuery);
};

export function AgentsGrid({ companyName }: { companyName: string }) {
  const navigate = useNavigate();
  const deviceSearch = useDebouncedFilter();
  const endOfMonth = getEndOfMonth();

  const filter = deviceSearch.filter
    ? { logic: "and" as const, filters: [{ field: "description", operator: "contains", value: deviceSearch.filter }] }
    : undefined;

  return (
    <div className="row mb-4">
      <div className="col-12">
        <DashboardSectionHeader
          title={`Agents — ${companyName}`}
          right={
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search Agents..."
              onChange={deviceSearch.onChange}
              style={{ width: 220 }}
            />
          }
        />
        <TitlelessCard className="p-0" bodyClassName="p-0">
          <ODataGrid
            getData={getClientSummaries}
            pageSize={10}
            pageable={true}
            filterable={false}
            filter={filter}
            onRowClick={(e) => navigate(`/cteye/clients/${e.dataItem.id}`)}
          >
            <ResponsiveGridColumn 
              title="Agent" 
              field="description" 
              sortable={true} 
              filterable={false} 
            />
            <ResponsiveGridColumn
              title="Inspections This Month"
              field="inspectionsUsedThisMonth"
              sortable={false}
              filterable={false}
              width={250}
              breakpoint={Breakpoints.lg}
              cell={(props) => (
                <td>
                  <UsageSparkline
                    used={props.dataItem.inspectionsUsedThisMonth}
                    limit={props.dataItem.monthlyInspectionLimit}
                    label="Inspections"
                    resetDate={endOfMonth}
                  />
                </td>
              )}
            />
            <ResponsiveGridColumn
              title="Reports This Month"
              field="reportsUsedThisMonth"
              sortable={false}
              filterable={false}
              width={250}
              breakpoint={Breakpoints.xl}
              cell={(props) => (
                <td>
                  <UsageSparkline
                    used={props.dataItem.reportsUsedThisMonth}
                    limit={props.dataItem.monthlyReportLimit}
                    label="Reports"
                    resetDate={endOfMonth}
                  />
                </td>
              )}
            />
            <ResponsiveGridColumn
              title="Online"
              field="isOnline"
              sortable={false}
              filterable={false}
              width={120}
              cell={(props) => (
                <td>
                  <DotBadge
                    variant={props.dataItem.isOnline ? "active" : "inactive"}
                    label={props.dataItem.isOnline ? "Online" : "Offline"}
                  />
                </td>
              )}
            />
            <ResponsiveGridColumn
              title="Last Seen"
              field="lastSeenDate"
              sortable={false}
              filterable={false}
              width={200}
              breakpoint={Breakpoints.md}
              cell={(props) => (
                <td className="text-secondary small">
                  {props.dataItem.lastSeenDate ? formatDateTime(new Date(props.dataItem.lastSeenDate)) : "—"}
                </td>
              )}
            />
          </ODataGrid>
        </TitlelessCard>
      </div>
    </div>
  );
}